import { Signal, signal } from '@angular/core';

export interface VisualViewportState {
  /** Visual-viewport height; on a phone with the keyboard up it is the free area. `null` without the API. */
  height: number | null;
  /** How much of the layout viewport the keyboard covers. */
  keyboard: number;
}

let state: Signal<VisualViewportState> | undefined;

/** One app-wide signal over `window.visualViewport`; the listener lives as long as the page. */
export function visualViewport(): Signal<VisualViewportState> {
  if (state) return state;
  const viewport = typeof window === 'undefined' ? null : window.visualViewport;
  // `scroll` fires on every pan: an unchanged reading must not wake the consumers (the editor reframes on each wake).
  const value = signal<VisualViewportState>(
    { height: null, keyboard: 0 },
    { equal: (a, b) => a.height === b.height && a.keyboard === b.keyboard },
  );
  state = value.asReadonly();
  if (!viewport) return state;

  const measure = (): void =>
    value.set({
      height: viewport.height,
      keyboard: Math.max(0, Math.round(window.innerHeight - viewport.height - viewport.offsetTop)),
    });
  measure();
  viewport.addEventListener('resize', measure);
  // iOS pans the visual viewport to a focused field (and a pinch pans it) with `scroll` alone: offsetTop moves, no resize.
  viewport.addEventListener('scroll', measure);
  return state;
}
